/**
 * ChapterView — renders the Reader's Normal-tab text with an optional
 * chapter sidebar / TOC. If `book.chapters` is empty or contains a single
 * "Full text" entry, falls back to a one-pane plain-text render so single-
 * file ebooks (poetry collections, short stories) keep working.
 *
 * Mobile: TOC collapses to a top-of-content drawer (Sheet) toggled by a
 * "Chapters · {title}" pill. Desktop: persistent sidebar.
 *
 * Persists the active chapter index per-book in localStorage so revisiting
 * a long book picks up exactly where the reader left off.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { List, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Button } from './ui/button';

const STORAGE_KEY = 'sl_reader_chapter_idx';

const loadIdx = (bookId) => {
  if (!bookId) return 0;
  try {
    const raw = localStorage.getItem(`${STORAGE_KEY}::${bookId}`);
    const n = parseInt(raw, 10);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  } catch {
    return 0;
  }
};

const saveIdx = (bookId, idx) => {
  if (!bookId) return;
  try {
    localStorage.setItem(`${STORAGE_KEY}::${bookId}`, String(idx));
  } catch {
    /* quota — ignore */
  }
};

const ChapterView = ({ book, chapterIdx: externalIdx, onChapterChange, hdProgressByIdx }) => {
  const chapters = useMemo(() => {
    const list = Array.isArray(book?.chapters) ? book.chapters : [];
    // Drop empty entries + the synthetic single-entry fallback the backend
    // returns when no chapter regex matches — in that case we just render
    // the flat content instead of a single-row TOC.
    if (
      list.length === 1 &&
      (list[0]?.title === 'Full text' || !list[0]?.title)
    ) {
      return [];
    }
    return list.filter((c) => c && c.content && c.content.trim());
  }, [book?.chapters]);

  const hasChapters = chapters.length > 1;
  const controlled = typeof onChapterChange === 'function';

  // Internal idx is only used when ChapterView is uncontrolled (no parent
  // owns it). When controlled, externalIdx is the source of truth — that's
  // how Reader's Listen / Flash / Chunk tabs stay synced with the active
  // chapter without circular updates.
  const [internalIdx, setInternalIdx] = useState(() => loadIdx(book?.book_id));
  const idx = controlled ? (externalIdx || 0) : internalIdx;
  const setIdx = (next) => {
    const value = typeof next === 'function' ? next(idx) : next;
    if (controlled) onChapterChange(value);
    else setInternalIdx(value);
  };

  const [drawerOpen, setDrawerOpen] = useState(false);
  const contentRef = useRef(null);
  const drawerRef = useRef(null);
  const drawerTriggerRef = useRef(null);

  useEffect(() => {
    if (!drawerOpen) return undefined;
    const previousFocus = document.activeElement;
    const drawerTrigger = drawerTriggerRef.current;
    const previousOverflow = document.body.style.overflow;
    const focusableSelector = 'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    const focusable = () => Array.from(drawerRef.current?.querySelectorAll(focusableSelector) || []);
    const focusFrame = window.requestAnimationFrame(() => focusable()[0]?.focus());
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setDrawerOpen(false);
        return;
      }
      if (event.key !== 'Tab') return;
      const nodes = focusable();
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
      (previousFocus || drawerTrigger)?.focus?.();
    };
  }, [drawerOpen]);

  // Reset idx when switching books, clamp if backend changed chapter count.
  useEffect(() => {
    if (controlled) return;
    const next = loadIdx(book?.book_id);
    setInternalIdx(Math.min(next, Math.max(0, chapters.length - 1)));
  }, [book?.book_id, chapters.length, controlled]);

  // Persist idx — works whether internal or external; we save whatever the
  // user is actually viewing.
  useEffect(() => {
    if (hasChapters) saveIdx(book?.book_id, idx);
  }, [idx, hasChapters, book?.book_id]);

  // Scroll to top of content area when the chapter changes — readers expect
  // a fresh page on chapter jump, not picking up wherever they left scrolled.
  useEffect(() => {
    if (contentRef.current) {
      const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      contentRef.current.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    }
  }, [idx]);

  // Fallback — single chapter / no detected splits → flat text.
  if (!hasChapters) {
    return (
      <article className="mx-auto max-w-[72ch] whitespace-pre-wrap text-[1.05rem] leading-8 text-white/85 sm:text-lg sm:leading-9">
        {book?.content}
      </article>
    );
  }

  const active = chapters[idx] || chapters[0];

  const TocList = ({ onPick }) => (
    <ul
      className="space-y-1 max-h-[60vh] lg:max-h-[70vh] overflow-y-auto pr-1"
      data-testid="reader-chapter-toc"
    >
      {chapters.map((c, i) => {
        const isActive = i === idx;
        const progress = (hdProgressByIdx && hdProgressByIdx[i]) || null;
        const ratioPct = progress ? Math.round(progress.ratio * 100) : 0;
        return (
          <li key={`${c.title}-${i}`}>
            <button
              type="button"
              onClick={() => {
                setIdx(i);
                onPick && onPick();
              }}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 ${
                isActive
                  ? 'bg-indigo-600/20 text-indigo-200 border border-indigo-500/40'
                  : 'text-gray-300 hover:bg-slate-700/60 hover:text-white border border-transparent'
              }`}
              data-testid={`reader-chapter-item-${i}`}
              aria-current={isActive ? 'page' : undefined}
            >
              <span
                className={`text-[10px] font-mono w-6 flex-shrink-0 ${
                  isActive ? 'text-indigo-300' : 'text-gray-500'
                }`}
              >
                {String(i + 1).padStart(2, '0')}
              </span>
              <span className="flex-1 min-w-0">
                <span className="flex items-center gap-1.5">
                  <span className="truncate">{c.title || `Chapter ${i + 1}`}</span>
                  {progress?.completed && (
                    <span
                      className="text-[9px] text-emerald-300 flex-shrink-0"
                      aria-label="Listened"
                      data-testid={`reader-chapter-completed-${i}`}
                    >✓</span>
                  )}
                  {progress?.isLive && !progress.completed && (
                    <span
                      className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse motion-reduce:animate-none flex-shrink-0"
                      aria-label="Now playing"
                      data-testid={`reader-chapter-live-${i}`}
                    />
                  )}
                </span>
                {progress && ratioPct > 0 && (
                  <span
                    className="mt-1 block h-0.5 w-full rounded-full bg-slate-700/70 overflow-hidden"
                    data-testid={`reader-chapter-progress-${i}`}
                  >
                    <span
                      className={`block h-full rounded-full transition-[width] duration-500 motion-reduce:transition-none ${
                        progress.completed ? 'bg-emerald-400/80' : 'bg-indigo-400'
                      }`}
                      style={{ width: `${Math.max(2, ratioPct)}%` }}
                    />
                  </span>
                )}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );

  return (
    <div className="flex flex-col lg:flex-row gap-6 not-prose">
      {/* Mobile chapter pill — persistent at top, opens drawer */}
      <div className="lg:hidden -mt-2">
        <button
          ref={drawerTriggerRef}
          type="button"
          onClick={() => setDrawerOpen(true)}
          className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-card border border-white/10 text-left hover:border-indigo-400/50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300"
          data-testid="reader-chapter-drawer-toggle"
          aria-expanded={drawerOpen}
          aria-controls="reader-chapter-drawer"
        >
          <div className="flex items-center gap-2 min-w-0">
            <List className="w-4 h-4 text-indigo-300 flex-shrink-0" aria-hidden="true" />
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.2em] text-indigo-300 font-semibold">
                Chapter {idx + 1} of {chapters.length}
              </p>
              <p className="text-sm font-semibold text-white truncate">
                {active.title}
              </p>
            </div>
          </div>
          <span className="text-xs text-white/50 flex-shrink-0">Choose</span>
        </button>
      </div>

      {/* Desktop sidebar */}
      <aside
        className="hidden lg:block w-64 flex-shrink-0"
        data-testid="reader-chapter-sidebar"
      >
        <div className="sticky top-4 rounded-xl bg-card/80 border border-white/10 p-3 shadow-[0_16px_40px_rgba(0,0,0,0.18)]">
          <p className="text-[10px] uppercase tracking-[0.2em] text-indigo-300 font-semibold px-2 mb-2">
            Chapters
          </p>
          <TocList />
        </div>
      </aside>

      {/* Mobile drawer overlay */}
      {drawerOpen && (
        <div
          className="lg:hidden fixed inset-0 z-50 bg-black/70 flex items-end"
          onClick={() => setDrawerOpen(false)}
          role="presentation"
        >
          <div
            ref={drawerRef}
            id="reader-chapter-drawer"
            className="w-full bg-card border-t border-white/10 rounded-t-2xl p-4 max-h-[75vh] overflow-hidden flex flex-col shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="reader-chapter-drawer"
            role="dialog"
            aria-modal="true"
            aria-labelledby="reader-chapter-dialog-title"
          >
            <div className="flex items-center justify-between mb-3">
              <p id="reader-chapter-dialog-title" className="text-sm font-semibold text-white">Chapters</p>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="text-gray-400 hover:text-white p-1"
                aria-label="Close chapter list"
              >
                <X className="w-5 h-5" aria-hidden="true" />
              </button>
            </div>
            <TocList onPick={() => setDrawerOpen(false)} />
          </div>
        </div>
      )}

      {/* Content */}
      <article className="flex-1 min-w-0">
        <div ref={contentRef} className="mx-auto max-w-[72ch]">
          <h2
            className="font-display text-3xl sm:text-4xl font-black tracking-tight text-white mb-1"
            data-testid="reader-chapter-title"
          >
            {active.title}
          </h2>
          <p className="text-xs text-gray-500 mb-5">
            Chapter {idx + 1} of {chapters.length}
          </p>
          <div className="whitespace-pre-wrap text-[1.05rem] leading-8 text-white/85 sm:text-lg sm:leading-9">
            {active.content}
          </div>

          {/* Prev / Next navigation */}
          <div
            className="mt-10 pt-6 border-t border-slate-700 flex items-center justify-between gap-3"
            data-testid="reader-chapter-nav"
          >
            <Button
              variant="ghost"
              disabled={idx === 0}
              onClick={() => setIdx((i) => Math.max(0, i - 1))}
              className="text-gray-300 hover:text-white disabled:opacity-30"
              data-testid="reader-chapter-prev"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Previous
            </Button>
            <span className="text-xs text-gray-500 font-mono">
              {idx + 1} / {chapters.length}
            </span>
            <Button
              variant="ghost"
              disabled={idx === chapters.length - 1}
              onClick={() => setIdx((i) => Math.min(chapters.length - 1, i + 1))}
              className="text-gray-300 hover:text-white disabled:opacity-30"
              data-testid="reader-chapter-next"
            >
              Next
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </article>
    </div>
  );
};

export default ChapterView;
